import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Users, Tv, LogOut, ThumbsUp, MessageCircle, Share2, Image, Smile, Trash2, Settings, Edit, UserPlus, BookOpen, Star, Gamepad2, Trophy, Award, CheckCircle2, Compass, Flame, Shield, Bookmark, Radio, TrendingUp, X, Zap } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import axios from 'axios';
import { API_URL } from '../config/api';
import ProfileSettings from './ProfileSettings';
import Clubs from './Clubs';
import Notifications, { NotificationBell } from './Notifications';
import Chronicle from './Diary';
import Followers from './Followers';
import ReviewModal from './ReviewModal';
import DailyGames from './DailyGames';
import MovieStockGame from './MovieStockGame';
import Leaderboard from './Leaderboard';
import Badges from './Badges';
import Watchlist from './Watchlist';
import RealityHub from './RealityHub';

/* ====== Action Label Mapping ====== */
const ACTION_LABELS = {
  watch_movie: 'Watched a movie', watch_tv_episode: 'Watched TV episode', complete_series: 'Completed a series',
  write_review: 'Wrote a review', review_liked: 'Review liked', like_action: 'Liked a post',
  recommend: 'Made a recommendation', recommendation_accepted: 'Recommendation accepted',
  add_watchlist: 'Added to watchlist', complete_watchlist: 'Completed watchlist',
  create_post: 'Created a post', post_liked: 'Post liked', comment_post: 'Commented on post',
  share_club: 'Shared to club', join_club: 'Joined a club',
  play_daily_game: 'Played Film in Five', win_daily_game: 'Won Film in Five', perfect_score_game: 'Perfect score',
  participation_game: 'Game participation', beat_challenge: 'Beat a challenge', send_challenge: 'Sent a challenge',
  diary_entry: 'Diary entry', rate_title: 'Rated a title',
  correct_prediction: 'Correct prediction', submit_prediction: 'Submitted prediction',
  submit_vote: 'Submitted vote', drama_rating: 'Drama rating',
  reality_game_correct: 'Correct reality game', reality_game_participation: 'Reality game played',
  correct_outcome_guess: 'Correct outcome guess',
};

/* ====== Points Log Modal ====== */
const PointsLogModal = ({ user, isOpen, onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (isOpen && user?.id) {
      setLoading(true);
      setError(false);
      setData(null);
      axios.get(`${API_URL}/api/points/${user.id}`)
        .then(res => setData(res.data))
        .catch(() => setError(true))
        .finally(() => setLoading(false));
    }
  }, [isOpen, user?.id]);

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-md bg-zinc-900 border-zinc-700 text-white p-0 max-h-[85dvh] overflow-y-auto rounded-xl" data-testid="points-log-modal">
        <DialogTitle className="sr-only">Points Log</DialogTitle>
        {/* Header */}
        <div className="sticky top-0 bg-zinc-900 z-10 px-3 sm:px-4 py-3 border-b border-zinc-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
              <Flame className="h-4 w-4 text-white" />
            </div>
            <div>
              <h2 className="text-sm sm:text-base font-bold">CasitaPoints</h2>
              <p className="text-[9px] sm:text-[10px] text-gray-500">Your earning history</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 sm:p-1.5 rounded-lg hover:bg-zinc-700 text-gray-400" data-testid="points-log-close">
            <X className="h-4 w-4 sm:h-5 sm:w-5" />
          </button>
        </div>

        <div className="p-3 sm:p-4">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <div className="animate-spin h-7 w-7 border-2 border-amber-500 border-t-transparent rounded-full" />
            </div>
          ) : error ? (
            <p className="text-center text-gray-500 py-8 text-sm">Failed to load points data</p>
          ) : data ? (
            <div className="space-y-4">
              {/* Total */}
              <div className="text-center bg-gradient-to-br from-amber-900/30 to-zinc-800 rounded-xl p-4 border border-amber-800/30">
                <p className="text-3xl sm:text-4xl font-black text-amber-400">{data.total_points}</p>
                <p className="text-xs text-gray-400 mt-1">Total CasitaPoints</p>
              </div>

              {/* Breakdown */}
              {data.breakdown && data.breakdown.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 mb-2">Breakdown</p>
                  <div className="space-y-1">
                    {data.breakdown.map(b => (
                      <div key={b.action} className="flex items-center justify-between px-3 py-2 bg-zinc-800 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] sm:text-xs text-white font-medium truncate">{ACTION_LABELS[b.action] || b.action}</p>
                          <p className="text-[9px] sm:text-[10px] text-gray-500">{b.count} time{b.count !== 1 ? 's' : ''}</p>
                        </div>
                        <span className="text-xs sm:text-sm font-bold text-amber-400 flex-shrink-0">+{b.total_points}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Activity */}
              {data.recent_activity && data.recent_activity.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 mb-2">Recent Activity</p>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {data.recent_activity.map(a => (
                      <div key={a.id} className="flex items-center gap-2 px-3 py-2 bg-zinc-800/60 rounded-lg">
                        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                          <Zap className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-amber-400" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-[11px] sm:text-xs text-white truncate">{ACTION_LABELS[a.action] || a.action}</p>
                          <p className="text-[9px] text-gray-600">{new Date(a.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</p>
                        </div>
                        <span className="text-[11px] sm:text-xs font-bold text-amber-400 flex-shrink-0">+{a.points}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(!data.breakdown || data.breakdown.length === 0) && (!data.recent_activity || data.recent_activity.length === 0) && (
                <div className="text-center py-6 text-gray-500">
                  <Flame className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-xs">No points earned yet. Start watching, reviewing & playing!</p>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500">
              <Flame className="h-8 w-8 mx-auto mb-2 opacity-30" />
              <p className="text-xs">No points data available</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

/* ====== Daily Games Summary Widget (matches dashboard design) ====== */
const DailyGamesSummary = ({ user, gameStatus, dashStats, onOpenGame }) => {
  // Read localStorage cache as fallback when API refresh fails (mobile/deployed)
  let cachedGame = null;
  try {
    const raw = localStorage.getItem('casitaplex-game-today');
    if (raw) cachedGame = JSON.parse(raw);
  } catch (_) { /* ignore */ }
  const todayStr = new Date().toISOString().split('T')[0];
  const cacheValid = cachedGame && cachedGame.date === todayStr && cachedGame.completed;

  const completed = gameStatus?.completed || cacheValid || false;
  const played = gameStatus?.played || cacheValid || false;
  const score = gameStatus?.score || (cacheValid ? cachedGame.score : 0);
  const streak = dashStats?.streak || 0;
  const totalPts = dashStats?.total_game_score || score;
  const totalPlayed = dashStats?.total_played || (played ? 1 : 0);
  const bestScore = dashStats?.best_score || score;

  return (
    <div className="rounded-xl overflow-hidden mb-3 sm:mb-4" data-testid="daily-games-widget">
      {/* Purple gradient header */}
      <div
        className="bg-gradient-to-r from-purple-600 to-indigo-600 px-3 sm:px-4 py-3 flex items-center justify-between cursor-pointer"
        onClick={onOpenGame}
        data-testid="daily-games-header"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <Gamepad2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
          </div>
          <div>
            <h3 className="text-white font-bold text-sm leading-tight">Daily Games</h3>
            <p className="text-white/70 text-[11px] leading-tight">
              {completed ? '1/1 played today' : played ? '0/1 completed today' : '0/1 played today'}
            </p>
          </div>
        </div>
        {streak > 0 && (
          <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15" data-testid="streak-badge">
            <Flame className="h-3.5 w-3.5 text-orange-300" />
            <span className="text-orange-300 text-xs font-bold">{streak}d</span>
          </div>
        )}
      </div>

      {/* Film in Five game row */}
      <div className="bg-zinc-800 px-3 sm:px-4 py-2.5 flex items-center justify-between border-b border-zinc-700/50">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-900/60 flex items-center justify-center flex-shrink-0">
            <Gamepad2 className="h-4 w-4 text-indigo-300" />
          </div>
          <div>
            <p className="text-white text-sm font-semibold leading-tight">Film in Five</p>
            <p className="text-gray-400 text-[11px] leading-tight">{score > 0 ? `${score} pts` : 'Guess the movie!'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {completed && (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          )}
          <button
            onClick={onOpenGame}
            className="px-4 sm:px-6 h-8 sm:h-9 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm font-semibold transition-colors"
            data-testid="daily-game-view-btn"
          >
            {completed ? 'View' : 'Play'}
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="bg-zinc-800 grid grid-cols-3 divide-x divide-zinc-700/50">
        <div className="py-2.5 text-center">
          <p className="text-green-400 text-base sm:text-lg font-bold" data-testid="game-stat-pts">{totalPts}</p>
          <p className="text-gray-500 text-[10px] sm:text-xs">Pts</p>
        </div>
        <div className="py-2.5 text-center">
          <p className="text-red-400 text-base sm:text-lg font-bold" data-testid="game-stat-played">{totalPlayed}</p>
          <p className="text-gray-500 text-[10px] sm:text-xs">Played</p>
        </div>
        <div className="py-2.5 text-center">
          <p className="text-green-400 text-base sm:text-lg font-bold" data-testid="game-stat-best">{bestScore}</p>
          <p className="text-gray-500 text-[10px] sm:text-xs">Best</p>
        </div>
      </div>
    </div>
  );
};

const UserDashboard = ({ user, onLogout, autoOpenGame }) => {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [showProfileSettings, setShowProfileSettings] = useState(false);
  const [showClubs, setShowClubs] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showChronicle, setShowChronicle] = useState(false);
  const [showDailyGames, setShowDailyGames] = useState(!!autoOpenGame);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showBadges, setShowBadges] = useState(false);
  const [showFollowers, setShowFollowers] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showWatchlist, setShowWatchlist] = useState(false);
  const [showRealityHub, setShowRealityHub] = useState(false);
  const [showMovieStock, setShowMovieStock] = useState(false);
  const [showPointsLog, setShowPointsLog] = useState(false);
  const [hasActiveXchangeGames, setHasActiveXchangeGames] = useState(false);
  const [expandedComments, setExpandedComments] = useState({});
  const [commentText, setCommentText] = useState({});
  const [commentPosting, setCommentPosting] = useState(null);
  const [realityFlags, setRealityFlags] = useState({});
  const [userClubs, setUserClubs] = useState([]);
  const [gameStatus, setGameStatus] = useState(null);
  const [dashStats, setDashStats] = useState(null);
  const [badgeSummary, setBadgeSummary] = useState(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [currentUser, setCurrentUser] = useState(user);
  const navigate = useNavigate();

  useEffect(() => { setCurrentUser(user); }, [user]);

  const handleUpdateUser = (updatedUser) => {
    setCurrentUser(updatedUser);
    localStorage.setItem('casitaplex-user', JSON.stringify(updatedUser));
  };

  const fetchNotificationCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications/count?user_id=${currentUser.id}`);
      setNotificationCount(res.data.count);
    } catch (e) { /* ignore */ }
  }, [currentUser.id]);

  const fetchGameStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/games/daily/status?user_id=${currentUser.id}`);
      setGameStatus(res.data);
    } catch (e) { console.warn('fetchGameStatus failed:', e.message); }
  }, [currentUser.id]);

  const fetchDashStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/dashboard-stats/${currentUser.id}`);
      setDashStats(res.data);
    } catch (e) { console.warn('fetchDashStats failed:', e.message); }
  }, [currentUser.id]);

  const fetchBadgeSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/badges/${currentUser.id}`);
      if (res.data) {
        setBadgeSummary({
          game: (res.data.game_badges || []).filter(b => b.earned).length,
          movie: (res.data.movie_badges || []).filter(b => b.earned).length,
          genre: (res.data.genre_badges || []).filter(b => b.earned).length,
        });
      }
    } catch (e) { /* ignore */ }
  }, [currentUser.id]);

  const fetchPosts = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/posts?user_id=${currentUser.id}`);
      setPosts(res.data);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [currentUser.id]);

  const fetchUserClubs = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clubs?user_id=${currentUser.id}`);
      const allClubs = res.data || [];
      setUserClubs(allClubs.filter(c => c.members?.includes(currentUser.id) || c.owner_id === currentUser.id));
    } catch (e) { /* ignore */ }
  }, [currentUser.id]);

  useEffect(() => {
    fetchPosts();
    fetchUserClubs();
    fetchNotificationCount();
    fetchGameStatus();
    fetchDashStats();
    fetchBadgeSummary();
    // Check if Movie Xchange has active games
    axios.get(`${API_URL}/api/movie-stock/games`).then(res => {
      const games = res.data?.games || [];
      setHasActiveXchangeGames(games.length > 0);
    }).catch(() => {});
    // Fetch reality feature flags
    axios.get(`${API_URL}/api/reality-hub/feature-flags`).then(res => setRealityFlags(res.data || {})).catch(() => {});
    const interval = setInterval(fetchNotificationCount, 30000);

    // Mobile browsers (iOS/Android) suspend background updates.
    // Refresh stats whenever the page becomes visible or regains focus.
    const refreshAll = () => {
      fetchGameStatus();
      fetchDashStats();
      fetchBadgeSummary();
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refreshAll();
    };
    const handleFocus = () => refreshAll();

    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('pageshow', handleFocus);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('pageshow', handleFocus);
    };
  }, [fetchPosts, fetchUserClubs, fetchNotificationCount, fetchGameStatus, fetchDashStats, fetchBadgeSummary]);

  const handleNotificationsClose = () => {
    setShowNotifications(false);
    fetchNotificationCount();
  };

  const handleGameClose = () => {
    setShowDailyGames(false);
    // Immediate fetch + delayed retry for mobile browsers that
    // may still be animating the modal dismissal
    fetchGameStatus();
    fetchDashStats();
    fetchBadgeSummary();
    setTimeout(() => {
      fetchGameStatus();
      fetchDashStats();
      fetchBadgeSummary();
    }, 800);
  };

  const handleGameComplete = useCallback(() => {
    // Aggressive refresh with staggered retries for mobile
    const refresh = () => {
      fetchGameStatus();
      fetchDashStats();
      fetchBadgeSummary();
    };
    refresh();
    setTimeout(refresh, 500);
    setTimeout(refresh, 1500);
    setTimeout(refresh, 3000);
  }, [fetchGameStatus, fetchDashStats, fetchBadgeSummary]);

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API_URL}/api/posts?user_id=${user.id}`, { content: newPost });
      if (res.data.status === 'success') { setNewPost(''); fetchPosts(); }
    } catch (e) { /* ignore */ }
    finally { setPosting(false); }
  };

  const handleLike = async (postId) => {
    try {
      const res = await axios.post(`${API_URL}/api/posts/${postId}/like?user_id=${user.id}`);
      if (res.data.status === 'success') {
        setPosts(posts.map(post => {
          if (post.id === postId) {
            const likes = res.data.liked
              ? [...(post.likes || []), user.id]
              : (post.likes || []).filter(id => id !== user.id);
            return { ...post, likes };
          }
          return post;
        }));
      }
    } catch (e) { /* ignore */ }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/api/posts/${postId}?user_id=${user.id}`);
      setPosts(posts.filter(p => p.id !== postId));
    } catch (e) { /* ignore */ }
  };

  const toggleComments = (postId) => {
    setExpandedComments(prev => ({ ...prev, [postId]: !prev[postId] }));
  };

  const handleAddComment = async (postId) => {
    const text = (commentText[postId] || '').trim();
    if (!text) return;
    setCommentPosting(postId);
    try {
      const res = await axios.post(`${API_URL}/api/posts/${postId}/comment?user_id=${user.id}`, { content: text });
      if (res.data.status === 'success') {
        setPosts(posts.map(post => {
          if (post.id === postId) {
            return { ...post, comments: [...(post.comments || []), res.data.comment] };
          }
          return post;
        }));
        setCommentText(prev => ({ ...prev, [postId]: '' }));
      }
    } catch (e) { console.error(e); }
    finally { setCommentPosting(null); }
  };

  const handleShare = async (post) => {
    const shareText = post.content_title
      ? `Check out this review of "${post.content_title}" on CasitaPlex!`
      : `${post.content?.substring(0, 100)}...`;
    const shareUrl = window.location.origin;

    if (navigator.share) {
      try {
        await navigator.share({ title: 'CasitaPlex', text: shareText, url: shareUrl });
      } catch (e) { /* user cancelled */ }
    } else {
      try {
        await navigator.clipboard.writeText(`${shareText}\n${shareUrl}`);
        alert('Link copied to clipboard!');
      } catch (e) {
        alert('Unable to share. Copy the link manually.');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('casitaplex-user');
    onLogout();
    navigate('/');
  };

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    const diff = Date.now() - d;
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m`;
    const h = Math.floor(diff / 3600000);
    if (h < 24) return `${h}h`;
    const days = Math.floor(diff / 86400000);
    if (days < 7) return `${days}d`;
    return d.toLocaleDateString();
  };

  const getInitials = (fn, ln) => `${fn?.[0] || ''}${ln?.[0] || ''}`.toUpperCase();

  return (
    <div className="min-h-screen bg-zinc-900 pb-14 lg:pb-0 overflow-x-hidden">
      {/* ======== TOP NAV ======== */}
      <header className="fixed top-0 left-0 right-0 bg-zinc-800 border-b border-zinc-700 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 h-12 sm:h-14 flex items-center justify-between">
          {/* Left: logo */}
          <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
            <a href="/browse" className="text-lg sm:text-2xl font-bold text-red-600 whitespace-nowrap">CASITAPLEX</a>
            <div className="hidden md:flex items-center bg-zinc-700 rounded-full px-4 py-1.5">
              <input type="text" placeholder="Search Casitaplex" className="bg-transparent text-white placeholder-gray-400 outline-none w-40 text-sm" />
            </div>
          </div>

          {/* Center nav - desktop only */}
          <nav className="hidden sm:flex items-center gap-1">
            <button className="p-2.5 rounded-lg bg-red-600/20 text-red-500"><Home className="h-5 w-5" /></button>
            <button className="p-2.5 rounded-lg hover:bg-zinc-700 text-gray-400"><Users className="h-5 w-5" /></button>
            <a href="/browse" className="p-2.5 rounded-lg hover:bg-zinc-700 text-gray-400"><Tv className="h-5 w-5" /></a>
            <button onClick={() => setShowClubs(true)} className="p-2.5 rounded-lg hover:bg-zinc-700 text-gray-400"><UserPlus className="h-5 w-5" /></button>
          </nav>

          {/* Right: points, admin, followers, settings, bell, profile, logout */}
          <div className="flex items-center gap-1 sm:gap-2.5 flex-shrink-0">
            {/* Points button */}
            <button
              onClick={() => setShowPointsLog(true)}
              className="flex items-center gap-1 px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-full bg-amber-900/30 border border-amber-800/40 hover:bg-amber-900/50 transition-colors"
              title="View Points Log"
              data-testid="header-points-btn"
            >
              <Flame className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-amber-400" />
              <span className="text-[11px] sm:text-xs font-bold text-amber-400">{dashStats?.casita_points ?? 0}</span>
            </button>
            {currentUser.role === 'admin' && (
              <button onClick={() => navigate('/admin')} className="p-1.5 sm:p-2 rounded-full hover:bg-zinc-700 text-amber-400" title="Administration" data-testid="admin-button">
                <Shield className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            )}
            <button onClick={() => setShowFollowers(true)} className="p-1.5 sm:p-2 rounded-full hover:bg-zinc-700 text-gray-400" title="Followers" data-testid="header-followers-button">
              <Users className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            <button onClick={() => setShowProfileSettings(true)} className="p-1.5 sm:p-2 rounded-full hover:bg-zinc-700 text-gray-400" title="Settings" data-testid="settings-button">
              <Settings className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
            <NotificationBell user={currentUser} onClick={() => setShowNotifications(true)} count={notificationCount} />
            <button onClick={() => setShowProfileSettings(true)} className="cursor-pointer hover:ring-2 hover:ring-zinc-600 rounded-full" title="Profile" data-testid="profile-button">
              <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-full bg-red-600 flex items-center justify-center text-white text-[10px] sm:text-sm font-semibold overflow-hidden">
                {currentUser.profile_picture
                  ? <img src={currentUser.profile_picture} alt="" className="w-full h-full object-cover" />
                  : getInitials(currentUser.first_name, currentUser.last_name)
                }
              </div>
            </button>
            <button onClick={handleLogout} className="p-1.5 sm:p-2 rounded-full hover:bg-zinc-700 text-gray-400" title="Logout" data-testid="logout-button">
              <LogOut className="h-4 w-4 sm:h-5 sm:w-5" />
            </button>
          </div>
        </div>
      </header>

      {/* ======== MAIN CONTENT ======== */}
      <div className="pt-12 sm:pt-14 flex">
        {/* LEFT SIDEBAR - desktop */}
        <aside className="hidden lg:block w-64 fixed left-0 top-14 bottom-0 p-4 overflow-y-auto">
          <nav className="space-y-1">
            <button onClick={() => navigate('/profile')} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 text-white text-left" data-testid="sidebar-profile-link">
              <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center text-white font-semibold overflow-hidden">
                {currentUser.profile_picture ? <img src={currentUser.profile_picture} alt="" className="w-full h-full object-cover" /> : getInitials(currentUser.first_name, currentUser.last_name)}
              </div>
              <div className="flex-1 min-w-0">
                <span className="block truncate text-sm">{currentUser.display_name || `${currentUser.first_name} ${currentUser.last_name}`}</span>
                {currentUser.bio && <span className="block text-xs text-gray-500 truncate">{currentUser.bio}</span>}
              </div>
            </button>
            {[
              { icon: UserPlus, label: 'Clubs', color: 'text-purple-500', onClick: () => setShowClubs(true) },
              { icon: Users, label: 'Friends', color: 'text-blue-500' },
              { icon: Compass, label: 'Discovery', color: 'text-red-500', href: '/browse', testid: 'sidebar-discovery' },
              { icon: BookOpen, label: 'Chronicle', color: 'text-yellow-500', onClick: () => setShowChronicle(true), testid: 'sidebar-chronicle' },
              { icon: Gamepad2, label: 'Daily Games', color: 'text-amber-500', onClick: () => setShowDailyGames(true), testid: 'sidebar-daily-games' },
              { icon: Trophy, label: 'Leaderboard', color: 'text-orange-500', onClick: () => setShowLeaderboard(true), testid: 'sidebar-leaderboard' },
              { icon: Award, label: 'Badges', color: 'text-purple-500', onClick: () => setShowBadges(true), testid: 'sidebar-badges' },
              { icon: Bookmark, label: 'Watchlist', color: 'text-amber-500', onClick: () => setShowWatchlist(true), testid: 'sidebar-watchlist' },
              { icon: Radio, label: 'Reality Hub', color: 'text-red-500', onClick: () => setShowRealityHub(true), testid: 'sidebar-reality-hub', hidden: !realityFlags.reality_hub_enabled },
            ].filter(item => !item.hidden).map((item, i) => {
              const Icon = item.icon;
              if (item.href) {
                return (
                  <a key={i} href={item.href} className="flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 text-gray-300">
                    <Icon className={`h-6 w-6 ${item.color}`} /><span className="text-sm">{item.label}</span>
                  </a>
                );
              }
              return (
                <button key={i} onClick={item.onClick} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-zinc-800 text-gray-300" data-testid={item.testid}>
                  <Icon className={`h-6 w-6 ${item.color}`} /><span className="text-sm">{item.label}</span>
                </button>
              );
            })}
          </nav>
          {currentUser.favorite_genres?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-zinc-700">
              <h4 className="text-gray-500 text-xs uppercase tracking-wide mb-2">Favorite Genres</h4>
              <div className="flex flex-wrap gap-1">
                {currentUser.favorite_genres.slice(0, 5).map(gid => {
                  const gn = { 28:'Action',12:'Adventure',16:'Animation',35:'Comedy',80:'Crime',99:'Documentary',18:'Drama',10751:'Family',14:'Fantasy',36:'History',27:'Horror',10402:'Music',9648:'Mystery',10749:'Romance',878:'Sci-Fi',53:'Thriller',10752:'War',37:'Western' };
                  return <span key={gid} className="px-2 py-0.5 bg-zinc-800 rounded text-xs text-gray-400">{gn[gid]||gid}</span>;
                })}
              </div>
            </div>
          )}
        </aside>

        {/* ======== FEED ======== */}
        <main className="flex-1 lg:ml-64 lg:mr-80 px-2 sm:px-4 py-3 sm:py-6 max-w-2xl mx-auto w-full">
          {/* Daily Games Widget - Inline */}
          <div className="rounded-xl overflow-hidden mb-2.5 sm:mb-4" data-testid="daily-games-widget">
            <div
              className="bg-gradient-to-r from-purple-600 to-indigo-600 px-3 py-2.5 sm:py-3 flex items-center justify-between cursor-pointer"
              onClick={() => setShowDailyGames(true)}
              data-testid="daily-games-header"
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
                  <Gamepad2 className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-sm leading-tight">Daily Games</h3>
                  <p className="text-white/70 text-[11px] leading-tight">
                    {gameStatus?.completed ? '1/1 played today' : gameStatus?.played ? '1/1 in progress' : '0/1 played today'}
                  </p>
                </div>
              </div>
              {(dashStats?.streak || 0) > 0 && (
                <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/15" data-testid="streak-badge">
                  <Flame className="h-3.5 w-3.5 text-orange-300" />
                  <span className="text-orange-300 text-xs font-bold">{dashStats.streak}d</span>
                </div>
              )}
            </div>
            <div className="bg-zinc-800 px-3 py-2 sm:py-2.5 flex items-center justify-between border-b border-zinc-700/50">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-indigo-900/60 flex items-center justify-center flex-shrink-0">
                  <Gamepad2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-indigo-300" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold leading-tight">Film in Five</p>
                  <p className="text-gray-400 text-[11px] leading-tight">{(gameStatus?.score || 0) > 0 ? `${gameStatus.score} pts` : 'Guess the movie!'}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {gameStatus?.completed && <CheckCircle2 className="h-4 w-4 text-green-500" />}
                <button
                  onClick={() => setShowDailyGames(true)}
                  className="px-3 sm:px-5 h-7 sm:h-8 rounded-md bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold transition-colors"
                  data-testid="daily-game-view-btn"
                >
                  {gameStatus?.completed ? 'View' : 'Play'}
                </button>
              </div>
            </div>
            <div className="bg-zinc-800 grid grid-cols-3 divide-x divide-zinc-700/50">
              <div className="py-2 sm:py-2.5 text-center">
                <p className="text-green-400 text-sm sm:text-lg font-bold" data-testid="game-stat-pts">{dashStats?.total_game_score || gameStatus?.score || 0}</p>
                <p className="text-gray-500 text-[10px]">Pts</p>
              </div>
              <div className="py-2 sm:py-2.5 text-center">
                <p className="text-red-400 text-sm sm:text-lg font-bold" data-testid="game-stat-played">{dashStats?.total_played || (gameStatus?.played ? 1 : 0)}</p>
                <p className="text-gray-500 text-[10px]">Played</p>
              </div>
              <div className="py-2 sm:py-2.5 text-center">
                <p className="text-green-400 text-sm sm:text-lg font-bold" data-testid="game-stat-best">{dashStats?.best_score || gameStatus?.score || 0}</p>
                <p className="text-gray-500 text-[10px]">Best</p>
              </div>
            </div>
            {/* Movie Xchange row — only show if there are active games */}
            {hasActiveXchangeGames && (
            <div className="bg-zinc-800 px-3 py-2 sm:py-2.5 flex items-center justify-between rounded-b-xl" data-testid="movie-stock-widget">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-emerald-900/60 flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-emerald-300" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold leading-tight">Movie Xchange</p>
                  <p className="text-gray-400 text-[11px] leading-tight">Predict &amp; Wager</p>
                </div>
              </div>
              <button
                onClick={() => setShowMovieStock(true)}
                className="px-3 sm:px-5 h-7 sm:h-8 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors"
                data-testid="movie-stock-play-btn"
              >
                Play
              </button>
            </div>
            )}
          </div>

          {/* Create Post */}
          <div className="bg-zinc-800 rounded-lg p-2.5 sm:p-4 mb-2.5 sm:mb-4">
            <div className="flex gap-2">
              <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-600 flex items-center justify-center text-white text-[10px] sm:text-sm font-semibold flex-shrink-0 overflow-hidden">
                {currentUser.profile_picture ? <img src={currentUser.profile_picture} alt="" className="w-full h-full object-cover" /> : getInitials(currentUser.first_name, currentUser.last_name)}
              </div>
              <button
                onClick={() => setShowReviewModal(true)}
                className="flex-1 bg-zinc-700 rounded-full px-4 py-2 text-left text-gray-400 text-sm hover:bg-zinc-600 transition-colors"
                data-testid="create-review-trigger"
              >
                Write a review, {currentUser.display_name || currentUser.first_name}...
              </button>
            </div>
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-700">
              <div className="flex gap-1">
                <button onClick={() => setShowReviewModal(true)} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-zinc-700 text-gray-400" data-testid="create-review-btn">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span className="text-xs">Review</span>
                </button>
                <button onClick={() => setShowReviewModal(true)} className="flex items-center gap-1 px-2 py-1 rounded-lg hover:bg-zinc-700 text-gray-400">
                  <Image className="h-4 w-4 text-green-500" />
                  <span className="hidden sm:inline text-xs">Photo</span>
                </button>
              </div>
            </div>
          </div>

          {/* Posts Feed */}
          {loading ? (
            <div className="text-center py-6">
              <div className="animate-spin h-6 w-6 border-2 border-red-600 border-t-transparent rounded-full mx-auto" />
              <p className="text-gray-400 mt-2 text-sm">Loading posts...</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="bg-zinc-800 rounded-lg p-5 sm:p-8 text-center">
              <p className="text-gray-400 text-sm">No posts yet. Be the first to share something!</p>
            </div>
          ) : (
            <div className="space-y-2.5 sm:space-y-4">
              {posts.map((post) => (
                <div key={post.id} className="bg-zinc-800 rounded-lg">
                  <div className="p-2.5 sm:p-4 flex items-start justify-between">
                    <div className="flex gap-2">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-red-600 flex items-center justify-center text-white text-[10px] sm:text-xs font-semibold flex-shrink-0">
                        {post.user?.profile_picture
                          ? <img src={post.user.profile_picture} alt="" className="w-full h-full rounded-full object-cover" />
                          : getInitials(post.user?.first_name, post.user?.last_name)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <p className="text-white text-sm font-semibold">{post.user?.first_name} {post.user?.last_name}</p>
                          {post.content_title && (
                            <span className="text-gray-400 text-xs">reviewed <span className="text-red-400 font-medium">{post.content_title}</span></span>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <p className="text-gray-400 text-[11px]">{formatDate(post.created_at)}</p>
                          {post.visibility && post.visibility !== 'public' && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-700 text-gray-400">
                              {post.visibility === 'followers' ? 'Followers' : post.visibility === 'club' ? 'Club' : 'Private'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {(post.user_id === currentUser.id || currentUser.role === 'admin') && (
                      <button onClick={() => handleDeletePost(post.id)} className="p-1 rounded-full hover:bg-zinc-700 text-gray-400 hover:text-red-500 flex-shrink-0">
                        <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    )}
                  </div>

                  {/* Rating */}
                  {post.rating > 0 && (
                    <div className="px-2.5 sm:px-4 pb-1 flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map(s => {
                        const full = post.rating >= s;
                        const half = !full && post.rating >= s - 0.5;
                        return (
                          <div key={s} className="relative w-3.5 h-3.5">
                            <Star className="absolute inset-0 w-full h-full text-zinc-600" />
                            {(full || half) && (
                              <div className="absolute inset-0 overflow-hidden" style={{ width: full ? '100%' : '50%' }}>
                                <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="px-2.5 sm:px-4 pb-2">
                    <p className="text-white text-sm whitespace-pre-wrap break-words">{post.content}</p>
                  </div>

                  {/* Movie poster or trailer */}
                  {post.content_poster && post.media_display !== 'trailer' && (
                    <div className="px-2.5 sm:px-4 pb-2">
                      <div className="bg-zinc-900 rounded-lg overflow-hidden flex justify-center p-2">
                        <img
                          src={`https://image.tmdb.org/t/p/w342${post.content_poster}`}
                          alt={post.content_title || ''}
                          className="max-h-64 object-contain rounded"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    </div>
                  )}
                  {post.media_display === 'trailer' && post.content_trailer && (
                    <div className="px-2.5 sm:px-4 pb-2">
                      <div className="bg-black rounded-lg overflow-hidden aspect-video">
                        <iframe src={post.content_trailer} title="Trailer" className="w-full h-full" allowFullScreen />
                      </div>
                    </div>
                  )}

                  {post.image && !post.content_poster && <img src={post.image} alt="" className="w-full" />}
                  {/* Like/comment counts */}
                  <div className="px-2.5 sm:px-4 py-1.5 flex items-center justify-between text-gray-400 text-[11px] sm:text-sm border-t border-zinc-700">
                    <div className="flex items-center gap-1">
                      {post.likes?.length > 0 && (
                        <><span className="w-4 h-4 bg-blue-600 rounded-full flex items-center justify-center"><ThumbsUp className="h-2.5 w-2.5 text-white" /></span><span>{post.likes.length}</span></>
                      )}
                    </div>
                    {post.comments?.length > 0 && (
                      <button onClick={() => toggleComments(post.id)} className="hover:underline">
                        {post.comments.length} comment{post.comments.length !== 1 ? 's' : ''}
                      </button>
                    )}
                  </div>
                  {/* Action buttons */}
                  <div className="px-1.5 sm:px-4 py-0.5 sm:py-1 flex border-t border-zinc-700">
                    <button onClick={() => handleLike(post.id)} className={`flex-1 flex items-center justify-center gap-1 py-1.5 sm:py-2 rounded-lg hover:bg-zinc-700 text-[11px] sm:text-sm ${post.likes?.includes(currentUser.id) ? 'text-blue-500 font-semibold' : 'text-gray-400'}`} data-testid={`like-btn-${post.id}`}>
                      <ThumbsUp className={`h-3.5 w-3.5 sm:h-5 sm:w-5 ${post.likes?.includes(currentUser.id) ? 'fill-blue-500' : ''}`} /><span>Like</span>
                    </button>
                    <button onClick={() => toggleComments(post.id)} className={`flex-1 flex items-center justify-center gap-1 py-1.5 sm:py-2 rounded-lg hover:bg-zinc-700 text-[11px] sm:text-sm ${expandedComments[post.id] ? 'text-blue-400' : 'text-gray-400'}`} data-testid={`comment-btn-${post.id}`}>
                      <MessageCircle className="h-3.5 w-3.5 sm:h-5 sm:w-5" /><span>Comment</span>
                    </button>
                    <button onClick={() => handleShare(post)} className="flex-1 flex items-center justify-center gap-1 py-1.5 sm:py-2 rounded-lg hover:bg-zinc-700 text-gray-400 text-[11px] sm:text-sm" data-testid={`share-btn-${post.id}`}>
                      <Share2 className="h-3.5 w-3.5 sm:h-5 sm:w-5" /><span>Share</span>
                    </button>
                  </div>
                  {/* Comments section */}
                  {expandedComments[post.id] && (
                    <div className="px-2.5 sm:px-4 pb-2.5 sm:pb-3 border-t border-zinc-700 pt-2" data-testid={`comments-section-${post.id}`}>
                      {(post.comments || []).length > 0 && (
                        <div className="space-y-2 mb-2 max-h-48 overflow-y-auto">
                          {(post.comments || []).map(c => (
                            <div key={c.id} className="flex gap-2">
                              <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-zinc-600 flex items-center justify-center text-[9px] sm:text-[10px] text-white font-semibold flex-shrink-0 overflow-hidden">
                                {c.user?.profile_picture
                                  ? <img src={c.user.profile_picture} alt="" className="w-full h-full object-cover" />
                                  : `${c.user?.first_name?.[0] || ''}${c.user?.last_name?.[0] || ''}`}
                              </div>
                              <div className="flex-1 min-w-0 bg-zinc-700/50 rounded-lg px-2.5 py-1.5">
                                <p className="text-[11px] sm:text-xs text-white font-semibold">{c.user?.first_name} {c.user?.last_name}</p>
                                <p className="text-[11px] sm:text-xs text-gray-300 break-words">{c.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2 items-center">
                        <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full bg-red-600 flex items-center justify-center text-[9px] sm:text-[10px] text-white font-semibold flex-shrink-0 overflow-hidden">
                          {currentUser.profile_picture
                            ? <img src={currentUser.profile_picture} alt="" className="w-full h-full object-cover" />
                            : getInitials(currentUser.first_name, currentUser.last_name)}
                        </div>
                        <input
                          type="text"
                          value={commentText[post.id] || ''}
                          onChange={(e) => setCommentText(prev => ({ ...prev, [post.id]: e.target.value }))}
                          onKeyDown={(e) => e.key === 'Enter' && handleAddComment(post.id)}
                          placeholder="Write a comment..."
                          className="flex-1 bg-zinc-700 border-none rounded-full px-3 py-1.5 text-[11px] sm:text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          data-testid={`comment-input-${post.id}`}
                        />
                        <button
                          onClick={() => handleAddComment(post.id)}
                          disabled={commentPosting === post.id || !(commentText[post.id] || '').trim()}
                          className="text-blue-500 hover:text-blue-400 disabled:text-gray-600 text-xs font-semibold flex-shrink-0"
                          data-testid={`comment-submit-${post.id}`}
                        >
                          {commentPosting === post.id ? '...' : 'Post'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </main>

        {/* RIGHT SIDEBAR - desktop */}
        <aside className="hidden lg:block w-80 fixed right-0 top-14 bottom-0 p-4 overflow-y-auto">
          {/* CasitaPoints Card */}
          <div className="bg-gradient-to-br from-amber-900/30 to-zinc-800 rounded-lg p-4 border border-amber-800/30" data-testid="sidebar-casita-points">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Flame className="h-4 w-4 text-amber-500" />CasitaPoints</h3>
              <span className="text-xs text-amber-400 font-medium bg-amber-900/40 px-2 py-0.5 rounded-full">Rank #{dashStats?.rank || '—'}</span>
            </div>
            <div className="text-3xl font-black text-amber-400 mb-1">{dashStats?.casita_points ?? 0}</div>
            <p className="text-gray-400 text-[11px]">Keep watching, reviewing & playing to earn more</p>
            <div className="grid grid-cols-3 gap-2 mt-3">
              <div className="bg-black/20 rounded-lg p-2 text-center">
                <p className="text-white font-bold text-sm">{dashStats?.total_played ?? 0}</p>
                <p className="text-gray-500 text-[10px]">Games</p>
              </div>
              <div className="bg-black/20 rounded-lg p-2 text-center">
                <p className="text-white font-bold text-sm">{dashStats?.total_game_score ?? 0}</p>
                <p className="text-gray-500 text-[10px]">Game Pts</p>
              </div>
              <div className="bg-black/20 rounded-lg p-2 text-center">
                <p className="text-white font-bold text-sm">{dashStats?.watch_streak ?? 0}</p>
                <p className="text-gray-500 text-[10px]">Streak</p>
              </div>
            </div>
          </div>

          {/* Badge Summary */}
          <div className="bg-zinc-800 rounded-lg p-4 mt-4" data-testid="sidebar-badge-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Award className="h-4 w-4 text-purple-500" />Badges</h3>
              <button onClick={() => setShowBadges(true)} className="text-xs text-purple-400 hover:text-purple-300" data-testid="sidebar-view-badges">View All</button>
            </div>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-purple-600 to-indigo-700 flex items-center justify-center text-lg font-black text-white">
                {dashStats?.badges_earned ?? 0}
              </div>
              <div>
                <p className="text-white text-sm font-semibold">{dashStats?.badges_earned ?? 0} of {dashStats?.total_badges ?? 30}</p>
                <p className="text-gray-500 text-[11px]">badges earned</p>
              </div>
            </div>
            <div className="space-y-2">
              {[
                { label: 'Games', icon: Gamepad2, color: 'text-amber-400 bg-amber-900/30', earned: badgeSummary?.game || 0, total: 5 },
                { label: 'Movies', icon: Star, color: 'text-red-400 bg-red-900/30', earned: badgeSummary?.movie || 0, total: 9 },
                { label: 'Genres', icon: Award, color: 'text-purple-400 bg-purple-900/30', earned: badgeSummary?.genre || 0, total: 16 },
              ].map(cat => (
                <div key={cat.label} className="flex items-center gap-2 text-xs">
                  <div className={`w-6 h-6 rounded flex items-center justify-center ${cat.color}`}><cat.icon className="h-3 w-3" /></div>
                  <span className="text-gray-300 flex-1">{cat.label}</span>
                  <span className="text-gray-400 font-medium">{cat.earned}/{cat.total}</span>
                  <div className="w-16 h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-600 to-purple-400 rounded-full" style={{ width: `${(cat.earned / cat.total) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Genre Challenges Progress */}
          {dashStats?.genre_challenges?.length > 0 && (
            <div className="bg-zinc-800 rounded-lg p-4 mt-4" data-testid="sidebar-genre-challenges">
              <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" />Genre Challenges</h3>
              <div className="space-y-2.5">
                {dashStats.genre_challenges.slice(0, 5).map((ch, i) => (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-gray-300 text-xs">{ch.name}</span>
                      <span className="text-gray-500 text-[10px]">{ch.progress}/{ch.target}</span>
                    </div>
                    <div className="w-full h-1.5 bg-zinc-700 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full transition-all ${ch.completed ? 'bg-green-500' : 'bg-amber-500'}`} style={{ width: `${Math.min((ch.progress / ch.target) * 100, 100)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Leaderboard */}
          <div className="bg-zinc-800 rounded-lg p-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-500" />Leaderboard</h3>
              <button onClick={() => setShowLeaderboard(true)} className="text-xs text-amber-400 hover:text-amber-300" data-testid="sidebar-view-leaderboard">View All</button>
            </div>
            <Button onClick={() => setShowLeaderboard(true)} className="w-full bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 border border-amber-700/40 text-sm" data-testid="sidebar-leaderboard-btn">
              <Trophy className="h-4 w-4 mr-2" />View Rankings
            </Button>
          </div>

          {/* Discovery */}
          <div className="bg-zinc-800 rounded-lg p-4 mt-4">
            <h3 className="text-white font-semibold text-sm mb-3 flex items-center gap-2"><Compass className="h-4 w-4 text-red-500" />Discovery</h3>
            <a href="/browse" className="block hover:bg-zinc-700 rounded-lg p-2 -mx-2" data-testid="sidebar-discovery-link">
              <div className="flex gap-3"><div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center"><Tv className="h-6 w-6 text-white" /></div><div><p className="text-white text-sm font-medium">Browse Content</p><p className="text-gray-400 text-xs">Movies & TV Shows</p></div></div>
            </a>
          </div>
        </aside>
      </div>

      {/* ======== MOBILE BOTTOM NAV ======== */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-zinc-800 border-t border-zinc-700 z-50 safe-area-bottom">
        <div className="flex items-center justify-around h-12">
          <button onClick={() => navigate('/browse')} className="flex flex-col items-center justify-center gap-0.5 p-1 text-gray-400" data-testid="mobile-nav-discover">
            <Compass className="h-4 w-4" /><span className="text-[9px]">Discover</span>
          </button>
          <button onClick={() => setShowWatchlist(true)} className="flex flex-col items-center justify-center gap-0.5 p-1 text-gray-400" data-testid="mobile-nav-watchlist">
            <Bookmark className="h-4 w-4" /><span className="text-[9px]">Watchlist</span>
          </button>
          <button onClick={() => setShowLeaderboard(true)} className="flex flex-col items-center justify-center gap-0.5 p-1 text-gray-400" data-testid="mobile-nav-ranks">
            <Trophy className="h-4 w-4" /><span className="text-[9px]">Leaderboard</span>
          </button>
          <button onClick={() => setShowBadges(true)} className="flex flex-col items-center justify-center gap-0.5 p-1 text-gray-400" data-testid="mobile-nav-badges">
            <Award className="h-4 w-4" /><span className="text-[9px]">Badges</span>
          </button>
          <button onClick={() => setShowClubs(true)} className="flex flex-col items-center justify-center gap-0.5 p-1 text-gray-400" data-testid="mobile-nav-clubs">
            <UserPlus className="h-4 w-4" /><span className="text-[9px]">Clubs</span>
          </button>
        </div>
      </nav>

      {/* ======== MODALS ======== */}
      <ProfileSettings isOpen={showProfileSettings} onClose={() => setShowProfileSettings(false)} user={currentUser} onUpdateUser={handleUpdateUser} />
      <Clubs user={currentUser} isOpen={showClubs} onClose={() => setShowClubs(false)} />
      <Notifications user={currentUser} isOpen={showNotifications} onClose={handleNotificationsClose} onNotificationCountChange={setNotificationCount} />
      <Chronicle user={currentUser} isOpen={showChronicle} onClose={() => setShowChronicle(false)} />
      <Followers user={currentUser} isOpen={showFollowers} onClose={() => setShowFollowers(false)} />
      <ReviewModal user={currentUser} isOpen={showReviewModal} onClose={() => setShowReviewModal(false)} onPostCreated={fetchPosts} clubs={userClubs} />
      <DailyGames user={currentUser} isOpen={showDailyGames} onClose={handleGameClose} onGameComplete={handleGameComplete} />
      <Leaderboard user={currentUser} isOpen={showLeaderboard} onClose={() => setShowLeaderboard(false)} />
      <Badges user={currentUser} isOpen={showBadges} onClose={() => { setShowBadges(false); fetchBadgeSummary(); }} />
      <Watchlist user={currentUser} isOpen={showWatchlist} onClose={() => setShowWatchlist(false)} />
      <RealityHub user={currentUser} isOpen={showRealityHub} onClose={() => setShowRealityHub(false)} />
      <MovieStockGame user={currentUser} isOpen={showMovieStock} onClose={() => setShowMovieStock(false)} />
      <PointsLogModal user={currentUser} isOpen={showPointsLog} onClose={() => setShowPointsLog(false)} />
    </div>
  );
};

export default UserDashboard;
